import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { colors, formatNumber } from '../styles/tokens'
import { Button, Input, Select } from './FormControls'
import { calculateDisposal } from '../lib/quantityEngine'
import { useDraftPersistence } from '../lib/useDraftPersistence'

const MODULE_DISPLAY_NAMES = { excavation: 'Excavation', concrete_volume: 'Concrete Volume' }
function buildOutputLabel(output) {
  if (!output) return ''
  const name = MODULE_DISPLAY_NAMES[output.module_key] || output.module_key
  return `${name} — ${Number(output.calculated_value).toFixed(2)} ${output.unit}`
}

export default function DisposalModulePanel({ scopeId, boqItemId, superseding, onClose, onError, saving, onSave }) {
  const draftKey = `draft:disposal:${scopeId || boqItemId || 'unknown'}`
  const [draft, setDraft, clearDraft] = useDraftPersistence(draftKey, { excavationSourceOutputId: null, selectedBackfillOutputIds: [], useOverride: false, disposalOverride: '' })
  const initial = superseding ? {
    excavationSourceOutputId: superseding.engineering_inputs?.excavationSourceOutputId || null,
    selectedBackfillOutputIds: superseding.engineering_inputs?.selectedBackfillOutputIds || [],
    useOverride: superseding.engineering_inputs?.disposalOverride != null,
    disposalOverride: superseding.engineering_inputs?.disposalOverride ?? '',
  } : draft

  const [excavationSourceOutputId, setExcavationSourceOutputIdRaw] = useState(initial.excavationSourceOutputId)
  const [selectedBackfillOutputIds, setSelectedBackfillOutputIdsRaw] = useState(new Set(initial.selectedBackfillOutputIds))
  const [useOverride, setUseOverrideRaw] = useState(initial.useOverride)
  const [disposalOverride, setDisposalOverrideRaw] = useState(initial.disposalOverride)
  const [availableOutputs, setAvailableOutputs] = useState({ excavation: [], backfill: [] })

  const mk = (setter, key) => (v) => { setter(v); if (!superseding) setDraft((d) => ({ ...d, [key]: v instanceof Set ? [...v] : v })) }
  const setExcavationSourceOutputId = mk(setExcavationSourceOutputIdRaw, 'excavationSourceOutputId')
  const setSelectedBackfillOutputIds = mk(setSelectedBackfillOutputIdsRaw, 'selectedBackfillOutputIds')
  const setUseOverride = mk(setUseOverrideRaw, 'useOverride')
  const setDisposalOverride = mk(setDisposalOverrideRaw, 'disposalOverride')

  useEffect(() => {
    if (!scopeId) return
    ;(async () => {
      const { data, error } = await supabase.from('resource_quantity_output').select('id, module_key, calculated_value, unit, engineering_inputs').eq('scope_id', scopeId).eq('is_active', true).eq('module_key', 'excavation')
      if (error) { onError(error.message); return }
      setAvailableOutputs({
        excavation: (data || []).filter((o) => o.engineering_inputs?.mode === 'excavation'),
        backfill: (data || []).filter((o) => o.engineering_inputs?.mode === 'backfilling'),
      })
    })()
  }, [scopeId, onError])

  const excavationSource = availableOutputs.excavation.find((o) => o.id === excavationSourceOutputId)
  const selectedBackfills = availableOutputs.backfill.filter((o) => selectedBackfillOutputIds.has(o.id))

  const result = useMemo(() => {
    try {
      if (useOverride) {
        return { value: calculateDisposal({ disposalOverride: disposalOverride === '' ? NaN : Number(disposalOverride), excavationVolume: excavationSource?.calculated_value, backfillVolumes: selectedBackfills.map((o) => ({ label: buildOutputLabel(o), value: Number(o.calculated_value) })) }), error: null }
      }
      if (!excavationSource) return { value: null, error: 'Select an Excavation source Output.' }
      if (selectedBackfillOutputIds.has(excavationSourceOutputId)) return { value: null, error: 'Self-reference: the Excavation source cannot also be a selected Backfill deduction.' }
      return { value: calculateDisposal({ excavationVolume: Number(excavationSource.calculated_value), backfillVolumes: selectedBackfills.map((o) => ({ label: buildOutputLabel(o), value: Number(o.calculated_value) })) }), error: null }
    } catch (e) { return { value: null, error: e.message } }
  }, [useOverride, disposalOverride, excavationSource, selectedBackfills, excavationSourceOutputId, selectedBackfillOutputIds])

  async function handleSave() {
    if (!result.value) return
    if (!useOverride && selectedBackfillOutputIds.has(excavationSourceOutputId)) { onError('Self-reference: the Excavation source cannot also be a selected deduction.'); return }
    const engineeringInputs = {
      excavationSourceOutputId, excavationSourceLabel: buildOutputLabel(excavationSource), selectedBackfillOutputIds: [...selectedBackfillOutputIds],
      frozenExcavationValue: excavationSource ? Number(excavationSource.calculated_value) : null,
      frozenBackfillValuesUsed: selectedBackfills.map((o) => ({ outputId: o.id, label: buildOutputLabel(o), value: Number(o.calculated_value) })),
      disposalOverride: useOverride ? Number(disposalOverride) : undefined,
    }
    const succeeded = await onSave({ calculatedValueTon: result.value.finalQuantity, unit: 'm3', geometrySnapshot: { items: [] }, engineeringInputs, breakdownSnapshot: result.value, takeoffItemIdForProvenance: null })
    if (succeeded !== false) clearDraft()
  }

  return (
    <div>
      <L label="Excavation Source (Saved, Active, same Scope)">
        <Select value={excavationSourceOutputId || ''} onChange={(e) => setExcavationSourceOutputId(e.target.value || null)} style={{ width: '100%' }}>
          <option value="">— Select —</option>
          {availableOutputs.excavation.map((o) => <option key={o.id} value={o.id}>{buildOutputLabel(o)}</option>)}
        </Select>
      </L>
      <L label="Selected Backfill Outputs (deducted — select explicitly)">
        <div style={{ border: `1px solid ${colors.border}`, borderRadius: 6, maxHeight: 100, overflowY: 'auto' }}>
          {availableOutputs.backfill.filter((o) => o.id !== excavationSourceOutputId).map((o) => (
            <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: `1px solid ${colors.border}`, fontSize: 12 }}>
              <input type="checkbox" checked={selectedBackfillOutputIds.has(o.id)} onChange={() => setSelectedBackfillOutputIds((prev) => { const n = new Set(prev); n.has(o.id) ? n.delete(o.id) : n.add(o.id); return n })} />
              {buildOutputLabel(o)}
            </label>
          ))}
        </div>
      </L>
      <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, margin: '10px 0' }}>
        <input type="checkbox" checked={useOverride} onChange={(e) => setUseOverride(e.target.checked)} /> Use Manual Override (takes precedence)
      </label>
      {useOverride && <L label="Disposal Override (m³)"><Input type="number" value={disposalOverride} onChange={(e) => setDisposalOverride(e.target.value)} style={{ width: '100%' }} /></L>}

      {result.value && (
        <div style={{ margin: '12px 0', padding: '10px 12px', background: '#F8FAFF', border: `1px solid ${colors.primary}`, borderRadius: 8 }}>
          {result.value.steps.map((s, i) => <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' }}><span style={{ color: colors.textSecondary }}>{s.label}</span><span style={{ fontWeight: 600 }}>{formatNumber(s.value)} {s.unit}</span></div>)}
        </div>
      )}
      {result.error && <div style={{ fontSize: 12, color: colors.textMuted, fontStyle: 'italic', marginBottom: 14 }}>{result.error}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {!superseding && <button onClick={() => { if (window.confirm('Discard draft?')) { clearDraft(); onClose() } }} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12 }}>Discard Draft</button>}
        <Button onClick={handleSave} disabled={!result.value || saving} style={{ marginLeft: 'auto' }}>{saving ? 'Saving…' : superseding ? 'Save New Version' : 'Save Output'}</Button>
      </div>
    </div>
  )
}
function L({ label, children }) { return <div style={{marginBottom:10}}><label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginBottom: 4 }}>{label}</label>{children}</div> }
